import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ControlContainer, NgForm} from '@angular/forms';
import {
  BondPaymentDto,
  CompanyDto,
  CompanyShareholderDto,
  CompanySimpleDto,
  ContractCompanyDto,
  ContractCoObligationCompanyDto,
  ContractVersionDto,
  DictionaryBaseDto,
  DictionaryDto,
  SearchCriteria,
  TemplateDto,
  TreatyBaseDto,
  TreatyVersionCriteriaDto,
  TreatyVersionSimpleDto,
} from '../../../bonding_shared/model/dtos';
import {BondGuiService} from '../services/bond-gui.service';
import {
  AppProperty,
  BondInvoicingSchema,
  BondStatus,
  BondSubType,
  ContractType,
  ReinsuranceMode,
  ReinsuranceType,
  CoObligationType,
  CompanyType,
  JOINT_VENTURES,
  BondDifferentPriceType,
} from '../../../bonding_shared/model/dictionary-ids';
import {DictionaryService} from '../../../bonding_shared/services/dictionary.service';
import {Country} from '../../../bonding_shared/model/country';
import {BondVersionDto, BondVersionSimpleDto} from '../../../bonding_shared/model';
import {RepositoryService} from '../../../bonding_shared/services/repository.service';
import {BondCustomFieldsComponent} from './shared/bond-custom-fields.component';
import {DocumentListComponent} from '../../document/components';
import {
  AppConfigService,
  GrowlService,
  PropertyService,
  TemplateService,
  TreatyVersionService,
} from '../../../bonding_shared/services';
import {CurrencyExchangeRateService} from '../../../bonding_shared/services/currency-exchange-rate.service';
import {BondValueListComponent} from './bond-value-list.component';
import {DictionaryUtils} from '../../../bonding_shared/utils/dictionary-utils';
import {BondPaymentListComponent} from './bond-payment-list.component';
import {DateUtils} from '../../../bonding_shared/utils/date-utils';
import {BusinessUnit} from '../../../bonding_shared/model/business-unit';
import SIGNER_TYPES = ContractType.SIGNER_TYPES;
import {DetailsView} from '../../../bonding_shared/components/details-view/details-view';

@Component({
  selector: 'bond-form',
  templateUrl: 'bond-form.component.html',
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}],
})
export class BondFormComponent extends DetailsView implements OnInit {
  self = this;
  public _bond: BondVersionDto;
  public _selectedCompany: CompanyDto;
  public _contractVersion: ContractVersionDto;
  private deliveryTypes: {[key: string]: DictionaryDto} = {};
  private customTemplate: TemplateDto;
  public originalSubtypeCustomTemplate: DictionaryBaseDto;
  private isCoObligationSectionVisible = false;
  private _isCoobligationCheck = false;
  readonly BondStatus = BondStatus;
  readonly BondInvoicingSchema = BondInvoicingSchema;
  readonly DictionaryUtils = DictionaryUtils;
  treaties: TreatyBaseDto[] = [];
  readonly SYSTEM_CURRENCY_CODE = this.propertyService.properties[AppProperty.SYSTEM_CURRENCY];
  readonly CompanyType = CompanyType;
  readonly ContractType = ContractType;
  readonly BondDifferentPriceType = BondDifferentPriceType;
  bondCoObligations: ContractCoObligationCompanyDto[] = [];
  @ViewChild('bondCustomFieldsComponent') bondCustomFieldsComponent: BondCustomFieldsComponent;
  @ViewChild('documentLists', {static: true}) documentListComponent: DocumentListComponent;

  @ViewChild('bondMultiValuesTable', {static: true}) bondMultiValuesTable: BondValueListComponent;
  @ViewChild('bondSupplementaryValuesTable', {static: true}) bondSupplementaryValuesTable: BondValueListComponent;
  @ViewChild('bondPaymentsTable', {static: true}) bondPaymentsTable: BondPaymentListComponent;

  @Input() set bond(bond: BondVersionDto) {
    this._bond = bond;
    if (bond) {
      this.showCoObligation();
    }
  }

  @Input() set showCoObligationSection(showCoObligationSection: boolean) {
    this.isCoObligationSectionVisible = showCoObligationSection;
  }

  @Input() set selectedCompany(selectedCompany: CompanyDto) {
    this._selectedCompany = selectedCompany;
  }

  @Input() set contractVersion(contractVersion: ContractVersionDto) {
    this._contractVersion = contractVersion;
    this.applyAutomaticReleaseDefaults();
  }

  @Input() set isCoobligationCheck(isCoobligationCheck: boolean) {
    this._isCoobligationCheck = isCoobligationCheck;
  }

  get showCoObligationSection() {
    return this.isCoObligationSectionVisible;
  }

  get bond() {
    return this._bond;
  }

  get selectedCompany() {
    return this._selectedCompany;
  }

  get contractVersion() {
    return this._contractVersion;
  }

  get isCoobligationCheck() {
    return this._isCoobligationCheck;
  }

  get hasCoObligations(): boolean {
    return (this.bond?.bondCoObligations?.length ?? 0) > 0;
  }

  get isMGAType(): boolean {
    return this.isType(ContractType.MGA_TYPES);
  }

  get isBoosterType(): boolean {
    return this.isType(ContractType.BOOSTER_TYPES);
  }

  get isAutomaticReleaseDisabled(): boolean {
    return (
      this.isMGAType ||
      this._bond?.status?.id === BondStatus.BOND_USER_ERROR ||
      this._bond?.status?.id === BondStatus.BOND_TO_BE_RELEASED
    );
  }

  @Input() showDetails = false;
  @Input() showErrors = false;
  @Input() isBooster: Function;
  @Input() loadTemplates: Function;
  @Input() bondVersions: BondVersionSimpleDto[];
  @Input() bondSubtypes: {[key: number]: DictionaryDto} = {};
  @Input() isCoInsurance = false;
  @Output() showCoobligationChange = new EventEmitter<boolean>();

  constructor(
    private dictService: DictionaryService,
    private repositoryService: RepositoryService,
    public guiService: BondGuiService,
    public appService: AppConfigService,
    private templateService: TemplateService,
    private currencyExchangeRateService: CurrencyExchangeRateService,
    private propertyService: PropertyService,
    public ngForm: NgForm,
    protected treatyService: TreatyVersionService,
    protected growlService: GrowlService
  ) {
    super(growlService);
    this.initializeSelectors();
  }

  ngOnInit() {
    this.form = this.ngForm.form;
    this.dictService.getDictionary('DeliveryType').subscribe((entries) => {
      for (const entry of entries) {
        this.deliveryTypes[entry.id] = entry;
      }
    });

    this.customTemplate = <TemplateDto>this.guiService.getCustomTemplate();
    if (this.customTemplate && !this.customTemplate.dictionarySelectors) {
      this.templateService.getById(this.customTemplate.id).subscribe((t) => {
        this.customTemplate = <TemplateDto>t;
      });
    }

    const treatyCriteria: SearchCriteria<TreatyVersionCriteriaDto> = <SearchCriteria<TreatyVersionCriteriaDto>>{};
    treatyCriteria.criteria = <TreatyVersionCriteriaDto>{};
    treatyCriteria.criteria.lastActivated = true;
    treatyCriteria.criteria.treaty = <TreatyBaseDto>{};
    treatyCriteria.criteria.treaty.reinsuranceTypes = [ReinsuranceType.FAC];
    this.treatyService.searchByCriteria(treatyCriteria).subscribe(
      (treatiesResult) => {
        this.treaties = treatiesResult.result.map((tv) => (<TreatyVersionSimpleDto>tv).treaty);
      },
      (error) => console.log('treaty load error: ', error)
    );

    // If co-obligations already exist force checkbox ON and disabled and If contract of type MGA then checkbox OFF and disabled
    this.showCoObligationSection = this.hasCoObligations && !this.isMGAType;
  }

  onCurrencyChanged(currency: DictionaryBaseDto) {
    if (this.bondMultiValuesTable) {
      this.bondMultiValuesTable.updateAllValuesInSysCurr();
    } else {
      this.updateSingleValueInSysCurr(this.bond.values[0].value);
    }
    if (this.bondSupplementaryValuesTable) {
      this.bondSupplementaryValuesTable.updateAllValuesInSysCurr();
    }
    if (this.bondPaymentsTable) {
      this.bondPaymentsTable.updateAllValuesInSysCurr();
    }
  }

  updateSingleValueInSysCurr(newValue: number) {
    if (!this.bond.currency) {
      return;
    }
    this.bond.values[0].valueInSysCurr = this.isSysCurr(this.bond.currency)
      ? newValue
      : this.currencyExchangeRateService.calculate(newValue, this.bond.currency.id);
  }

  updateTotalValueInSysCurr(newValue: number) {
    if (!this.bond.currency) {
      return;
    }
    this.bond.coInsuranceTotalValueEUR = this.isSysCurr(this.bond.currency)
      ? newValue
      : this.currencyExchangeRateService.calculate(newValue, this.bond.currency.id);
  }

  isSysCurr(currency: DictionaryBaseDto) {
    return currency?.code === this.SYSTEM_CURRENCY_CODE;
  }

  // used on bond value field (not in value table)
  isSingleValueEditable(statusChanged: boolean) {
    return (
      this.bond &&
      !this.bond.output.showMultiValues &&
      (!this.bond.status || BondGuiService.isBondRequest(this.bond, statusChanged))
    );
  }

  onLanguageChanged(language: DictionaryBaseDto) {
    this.loadTemplates();
  }

  onCourtCountryChange(country: DictionaryBaseDto) {
    this.bond.cityOfJurisdiction = <DictionaryBaseDto>{};
    this.bond.cityOfJurisdictionText = null;

    if (this.isCityFromDict(country)) {
      this.setDefaultCityOfJurisdiction(country);
    }
  }

  isCityFromDict(country: DictionaryBaseDto): boolean {
    return this.isGermanCountry(country) || this.isItalianCountry(country);
  }

  isGermanCountry(country: DictionaryBaseDto): boolean {
    const b = this.bond;
    return b && Country.isGermany(country);
  }

  isItalianCountry(country: DictionaryBaseDto): boolean {
    const b = this.bond;
    return b && Country.isItaly(country);
  }

  onValidFromChanged(validFrom: Date) {
    // if value table is not visible there is no way to change validFrom of bond value manually
    // so it is done automatically  here
    // this must be done so the validFrom validation passes
    if (this.isFirstVersion()) {
      this.bond.values[0].validFrom = validFrom;
    }
    this.recalculateBondValidTo();
  }

  isValidToDisabled(): boolean {
    return this.isBooster() === true && this.bond.bondUnlimited === true;
  }

  canProlong(): boolean {
    return this.bond.bondValidTo !== undefined && this.bond.bondValidTo !== null;
  }

  refreshAutoprolongation() {
    if (!this.canProlong()) {
      this.bond.autoprolongation.autoprolongate = false;
    }
  }

  setDefaultCityOfJurisdiction(country: DictionaryBaseDto) {
    if (Country.isItaly(country)) {
      this.bond.cityOfJurisdiction.id = Country.MILAN;
    }

    if (Country.isGermany(country)) {
      this.bond.cityOfJurisdiction.id = Country.KOLN;
    }

    if (Country.isBelgium(country)) {
      this.bond.cityOfJurisdiction.id = Country.BRUSSELS;
    }
  }

  isFirstVersion(): boolean {
    if (!this.bond.id) {
      // unsaved bond have no versionNumber
      return !this.bondVersions || this.bondVersions.length < 1;
    }
    return this.bond.versionNumber === 1;
  }

  onValidToChanged() {
    this.refreshAutoprolongation();
  }

  bondSubtypeHasCustomFields(): boolean {
    if (this.bond.subtype && this.bondSubtypes[this.bond.subtype.id]) {
      if (this.bond.subtype.id === BondSubType.TECHNICAL_USER_TEMPLATE) {
        if (this.isSetSubtypeFromCustomTemplate()) {
          return true;
        }
      } else {
        if (this.bondSubtypes[this.bond.subtype.id].relatedDictionaries['BondCustomFieldType'].length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  isSetSubtypeFromCustomTemplate() {
    if (
      this.customTemplate &&
      this.customTemplate.dictionarySelectors &&
      this.customTemplate.dictionarySelectors.filter((bs) => bs.dictName === 'BondSubtype').length === 1
    ) {
      this.originalSubtypeCustomTemplate = this.customTemplate.dictionarySelectors.find(
        (bs) => bs.dictName === 'BondSubtype'
      );
      if (
        this.bondSubtypes[this.originalSubtypeCustomTemplate.id].relatedDictionaries['BondCustomFieldType'].length > 0
      ) {
        return true;
      }
    }
  }

  isItalianPerformanceBond() {
    return this.bond.subtype && this.bond.subtype.id === BondSubType.PERFORMANCE_BOND;
  }

  acceptedConditionsDateVisible() {
    return (
      this.bond &&
      this.bond.contractType &&
      (this.bond.contractType.id === ContractType.INDIVIDUAL ||
        this.bond.contractType.id === ContractType.INDIVIDUAL_BOOSTER)
    );
  }

  acceptedConditionsDateRequired() {
    return (
      this.bond.id &&
      this.bond.status &&
      this.bond.status.id === BondStatus.BOND &&
      this.acceptedConditionsDateVisible()
    );
  }

  recalculateBondValidTo() {
    setTimeout(() => this.doRecalculateBondValidTo(), 0);
  }

  doRecalculateBondValidTo() {
    const damageClauseTypeCustomField = this.bondCustomFieldsComponent.getDamageClauseTypeCustomField();
    if (damageClauseTypeCustomField) {
      const isDamageClause =
        damageClauseTypeCustomField.value !== this.bondCustomFieldsComponent.NO_DAMAGE_CLAUSE.value;
      const lastPaymentDate = this.getLastPaymentDate();
      if (isDamageClause) {
        this.bond.bondValidTo = DateUtils.addMonth(lastPaymentDate, 12);
        this.bond.estimatedValidTo = true;
      } else {
        this.bond.bondValidTo = lastPaymentDate;
      }
    }
  }

  getLastPaymentDate() {
    const payments = this.bond.payments;
    if (payments && payments.length > 0) {
      const sortedPayments = payments.sort(DateUtils.sorter('expectedDate'));
      return this.getBondValueDate(sortedPayments[payments.length - 1]);
    } else {
      return null;
    }
  }

  getBondValueDate(payment: BondPaymentDto) {
    if (payment.effectiveDate) {
      return payment.effectiveDate;
    }
    return DateUtils.addDay(payment.expectedDate, this.bondCustomFieldsComponent.getPaymentTermCustomFieldValue());
  }

  getExecutionAddressLabelKey() {
    if (
      this.bond.subtype &&
      (this.bond.subtype.id === BondSubType.PROMESSE || this.bond.subtype.id === BondSubType.TIMBER_EU_PROMESSE)
    ) {
      return 'clientbond.details.beneficiary.placeOfSale';
    } else if (this.bond.subtype && this.bond.subtype.id === BondSubType.TIMBER_SALES) {
      return 'clientbond.details.beneficiary.salesAddress';
    } else {
      return 'clientbond.details.beneficiary.contractExecutionAddressOfBeneficiary';
    }
  }

  allowFacultativeTreaty(): boolean {
    return (
      this.bond &&
      this.bond.reinsuranceMode &&
      (this.bond.reinsuranceMode.id === ReinsuranceMode.FACULTATIVE ||
        this.bond.reinsuranceMode.id === ReinsuranceMode.MIXED)
    );
  }

  allowStandardTreaty(): boolean {
    return (
      this.bond &&
      this.bond.reinsuranceMode &&
      (this.bond.reinsuranceMode.id === ReinsuranceMode.MIXED ||
        this.bond.reinsuranceMode.id === ReinsuranceMode.FACULTATIVE ||
        this.bond.reinsuranceMode.id === ReinsuranceMode.STANDARD)
    );
  }

  get hiddenReinsuranceModeIds() {
    const ids = new Set<number>();
    ids.add(ReinsuranceMode.NONE);
    return ids;
  }

  showSigner() {
    const signerLogicFullyImplemented = true; // TODO: remove it when signer logic is fully implemented
    return (
      signerLogicFullyImplemented &&
      // as discussed with GC, business unit from contract is used for now, to be changed to signer business unit later
      SIGNER_TYPES.includes(this.bond.contractType?.id) &&
      (this.bond.contractBusinessUnit?.id === BusinessUnit.IT || this.bond.contractBusinessUnit?.id === BusinessUnit.DE)
    );
  }

  showCoObligation() {
    const hasCoobligation = this.bond.bondCoObligations && this.bond.bondCoObligations.length > 0;
    const hasJointVentureLegalForm =
      this.selectedCompany?.legalForm && JOINT_VENTURES.includes(this.selectedCompany.legalForm.id);
    if (hasCoobligation || hasJointVentureLegalForm) {
      this.showCoObligationSection = true;
    }
  }

  addCoObligator(company: CompanySimpleDto) {
    if (!this.bond.bondCoObligations || this.bond.bondCoObligations.length < 1) {
      this.bond.bondCoObligations = [];
    }
    const obligator = <ContractCoObligationCompanyDto>{company: company};
    this.bond.bondCoObligations.push(obligator);
  }

  private initializeSelectors(): void {
    this.selectorNameList = ['CoObligation'];
    this.initializeSelectorEmitters(true);
  }

  refreshCoobligationType() {
    if (!this.showCoObligationSection) {
      this.bond.coObligationType = null;
      this.bond.bondCoObligations = [];
    }
  }

  subinsuredCompanies(): CompanySimpleDto[] {
    return this._contractVersion?.contractCompanies?.map((s) => s.company);
  }

  onCheckboxChange(value: boolean) {
    this.isCoObligationSectionVisible = value;
    if (!this.isCoObligationSectionVisible) {
      this.bond.coObligationType = null;
      this.bond.bondCoObligations = [];
    }
    this.showCoobligationChange.emit(value);
  }

  isType(types: number[]): boolean {
    return this._contractVersion?.contract && DictionaryUtils.inArr(this._contractVersion.contract.type, types);
  }

  private applyAutomaticReleaseDefaults(): void {
    if (this.isMGAType || this.isBoosterType) {
      // MGA and Booster bonds: always unchecked by default
      this._bond.bond.automaticRelease = false;
    }
  }

  onSelectedDifferentPriceType(): void {
    if (this.bond?.autoprolongation) {
      this.bond.autoprolongation.autoprolongationPremiumAmount = null;
      this.bond.autoprolongation.autoprolongationPrice = null;
    }
  }

  isDifferentPriceType(differentPriceTypeIds: number[]): boolean {
    if (!this.bond?.autoprolongation) {
      return false;
    }
    return DictionaryUtils.inArr(this.bond.autoprolongation.autoprolongationPriceType, differentPriceTypeIds);
  }

  clearAutoprolongationPriceType(autoprolongationPriceType: boolean) {
    if (autoprolongationPriceType === true) {
      this.bond.autoprolongation.autoprolongationPremiumAmount = null;
      this.bond.autoprolongation.autoprolongationPrice = null;
      this.bond.autoprolongation.autoprolongationPriceType = null;
    }
  }

  get differentPriceTypeRegExp(): RegExp {
    return this._contractVersion &&
      DictionaryUtils.inArr(this._contractVersion.invoicingSchema, BondInvoicingSchema.FLAT_FEES)
      ? /^(?!.*PREMIUM_PERIOD).*$/
      : undefined;
  }
}
